import { MapPin, Target, Compass, Lightbulb, Flag } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import CtaSection from "@/components/home/CtaSection";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useSite, FALLBACK_SETTINGS } from "@/lib/content";

export default function About() {
  const { data } = useSite();
  const s = data?.settings || FALLBACK_SETTINGS;
  const seo = data?.settings?.seo?.["/about"];

  return (
    <>
      <Seo
        title={seo?.title || "About LogiTech Nexus | Technology & Software Company"}
        siteName="LogiTech Nexus"
        description={seo?.description || "LogiTech Nexus is a technology company building practical, scalable and intelligent software solutions that move businesses forward."}
      />
      <PageHero
        eyebrow="About LogiTech Nexus"
        title="Building Technology That Moves"
        accent="Businesses Forward."
        subtitle="LogiTech Nexus is a technology company focused on designing practical, scalable and intelligent digital solutions for businesses."
        testid="about-hero"
      />

      <section className="bg-void py-24 sm:py-32" data-testid="about-who-we-are">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <Eyebrow>Who We Are</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">
              A technology company built around real business needs.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-400">
              LogiTech Nexus designs and develops software for organizations that
              have outgrown off-the-shelf tools — or never fit into them in the
              first place. We work as a technology partner: understanding the
              business first, then engineering the systems that support it.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-400">
              Our name reflects how we think. A nexus is a point of connection —
              and great software is exactly that: people, data, systems and
              workflows connected into something that works as one.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="glass rounded-2xl p-8" data-testid="about-hq-card">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-blue-500/25 bg-blue-500/10 text-blue-400">
                  <MapPin size={20} />
                </span>
                <h3 className="font-display text-lg font-semibold text-white">Headquarters</h3>
              </div>
              <address className="mt-6 text-sm not-italic leading-relaxed text-slate-300">
                {s.companyName}
                <br />
                {s.addressLine1}
                <br />
                {s.addressLine2}
                <br />
                {s.addressCountry}
              </address>
              <p className="mt-4 border-t border-slate-800 pt-4 text-sm text-slate-400">
                Based in Tacoma, Washington — working with businesses locally and beyond.
              </p>
              <a
                href={`mailto:${s.email}`}
                data-testid="about-email-link"
                className="mt-4 inline-block text-sm font-semibold text-blue-400 transition-colors hover:text-blue-300"
              >
                {s.email}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-slate-800/60 bg-navy py-24 sm:py-32" data-testid="about-beliefs">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>What We Believe</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">
              Principles that shape every project.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Lightbulb, title: "Clarity over complexity", text: "Good software makes work simpler. We design for the people who use it every day." },
              { icon: Compass, title: "Business first, technology second", text: "The right solution starts with understanding the operation — not picking a framework." },
              { icon: Target, title: "Built to last", text: "Maintainable code, sound architecture and honest communication outlast shortcuts." },
            ].map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08} y={20}>
                <div className="h-full rounded-2xl border border-slate-800/80 bg-void/60 p-7" data-testid={`belief-card-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-blue-500/25 bg-blue-500/10 text-blue-400">
                    <b.icon size={20} />
                  </span>
                  <h3 className="mt-6 font-display text-lg font-semibold text-white">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-void py-24 sm:py-32" data-testid="about-approach">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <Eyebrow>Our Approach</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">
              Partnership, from first conversation to long after launch.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-400">
              We begin by understanding your business, your users and your
              constraints. Then we design, build, test and launch — and stay
              involved as the software evolves with your organization.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-blue-500/25 bg-gradient-to-br from-blue-500/[0.08] to-indigo-500/[0.04] p-8" data-testid="about-mission-card">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
                  <Flag size={20} />
                </span>
                <h3 className="font-display text-lg font-semibold text-white">Our Mission</h3>
              </div>
              <p className="mt-6 font-display text-xl leading-relaxed text-slate-100">
                "To help businesses turn ideas and operational challenges into
                powerful technology solutions that create lasting value."
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
